import React from 'react';
import { ExternalLink, Check, AlertTriangle, FileText, Scale, BookOpen, Clock } from 'lucide-react';

/**
 * Componente para mostrar una fuente legal verificada
 *
 * Muestra informacion de una fuente legal (ley, sentencia, etc.)
 * con su URL oficial para verificacion.
 *
 * Props:
 * - fuente: objeto con datos de la fuente
 * - compact: boolean - Modo compacto (default: false)
 * - showUrl: boolean - Mostrar URL (default: true)
 * - verificada: boolean - Si esta verificada (default: fuente.fue_verificada)
 */
function FuenteVerificada({
  fuente,
  compact = false,
  showUrl = true,
  verificada
}) {
  if (!fuente) return null;

  const esVerificada = verificada !== undefined ? verificada : (fuente.fue_verificada ?? true);

  // Iconos por tipo de fuente
  const getIcono = (tipo) => {
    switch (tipo?.toLowerCase()) {
      case 'ley':
        return Scale;
      case 'sentencia':
        return BookOpen;
      case 'reglamento':
      case 'real_decreto':
        return FileText;
      case 'orden':
        return FileText;
      default:
        return FileText;
    }
  };

  const Icono = getIcono(fuente.tipo || fuente.fuente_tipo);

  // URL a mostrar
  const url = fuente.url_verificable || fuente.fuente_url || fuente.url;
  const referencia = fuente.referencia || fuente.fuente_referencia || fuente.cita_texto;
  const titulo = fuente.titulo || fuente.fuente_titulo;

  // Modo compacto
  if (compact) {
    return (
      <span className={`
        inline-flex items-center gap-1.5 text-sm
        ${esVerificada ? 'text-green-700' : 'text-amber-700'}
      `}>
        {esVerificada ? (
          <Check className="w-3.5 h-3.5 text-green-600" />
        ) : (
          <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
        )}
        <span className="font-medium">{referencia}</span>
        {url && showUrl && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </span>
    );
  }

  // Modo completo
  return (
    <div className={`
      border rounded-lg p-3
      ${esVerificada
        ? 'bg-green-50 border-green-200'
        : 'bg-amber-50 border-amber-200'}
    `}>
      <div className="flex items-start gap-3">
        {/* Icono */}
        <div className={`
          p-2 rounded-lg flex-shrink-0
          ${esVerificada ? 'bg-green-100' : 'bg-amber-100'}
        `}>
          <Icono className={`w-4 h-4 ${esVerificada ? 'text-green-600' : 'text-amber-600'}`} />
        </div>

        {/* Contenido */}
        <div className="flex-1 min-w-0">
          {/* Referencia */}
          <div className="flex items-center gap-2 mb-1">
            <span className={`font-medium ${esVerificada ? 'text-green-900' : 'text-amber-900'}`}>
              {referencia}
            </span>
            {esVerificada ? (
              <span className="inline-flex items-center gap-1 text-xs bg-green-200 text-green-800 px-1.5 py-0.5 rounded">
                <Check className="w-3 h-3" /> Verificada
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded">
                <AlertTriangle className="w-3 h-3" /> Pendiente
              </span>
            )}
          </div>

          {/* Titulo */}
          {titulo && (
            <p className="text-sm text-gray-600 mb-2">{titulo}</p>
          )}

          {/* URL verificable */}
          {url && showUrl && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Verificar en fuente oficial
            </a>
          )}

          {/* Fecha de verificacion */}
          {fuente.fecha_verificacion && (
            <div className="flex items-center gap-1 text-xs text-gray-500 mt-2">
              <Clock className="w-3 h-3" />
              Verificado: {new Date(fuente.fecha_verificacion).toLocaleDateString()}
            </div>
          )}
        </div>
      </div>

      {/* Texto relevante si existe */}
      {fuente.texto_relevante && (
        <div className="mt-3 p-2 bg-white rounded border text-sm text-gray-700 italic">
          "{fuente.texto_relevante}"
        </div>
      )}
    </div>
  );
}

/**
 * Lista de fuentes verificadas
 */
export function ListaFuentesVerificadas({ fuentes = [], titulo = 'Fuentes Legales' }) {
  if (fuentes.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No hay fuentes legales registradas</p>
      </div>
    );
  }

  const verificadas = fuentes.filter(f => f.fue_verificada !== false);
  const sinVerificar = fuentes.filter(f => f.fue_verificada === false);

  return (
    <div>
      {/* Header con contador */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-gray-900">{titulo}</h3>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-green-600">
            {verificadas.length} verificadas
          </span>
          {sinVerificar.length > 0 && (
            <span className="text-amber-600">
              {sinVerificar.length} pendientes
            </span>
          )}
        </div>
      </div>

      {/* Lista de fuentes */}
      <div className="space-y-3">
        {fuentes.map((fuente, index) => (
          <FuenteVerificada
            key={fuente.id || index}
            fuente={fuente}
            verificada={fuente.fue_verificada}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Badge de estado de verificacion de citas
 */
export function VerificacionCitasBadge({ verificadas, total }) {
  const porcentaje = total > 0 ? Math.round((verificadas / total) * 100) : 100;

  let color, icon, texto;

  if (porcentaje === 100) {
    color = 'green';
    icon = Check;
    texto = 'Todas verificadas';
  } else if (porcentaje >= 50) {
    color = 'yellow';
    icon = AlertTriangle;
    texto = `${total - verificadas} pendientes`;
  } else {
    color = 'red';
    icon = AlertTriangle;
    texto = `${total - verificadas} sin verificar`;
  }

  const Icon = icon;

  const colorClasses = {
    green: 'bg-green-100 text-green-800 border-green-200',
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    red: 'bg-red-100 text-red-800 border-red-200'
  };

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${colorClasses[color]}`}>
      <Icon className="w-3.5 h-3.5" />
      {verificadas}/{total} citas - {texto}
    </span>
  );
}

export default FuenteVerificada;
